import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} GiftCardNoteRefs
 * @property {HTMLInputElement} fromInput - Who the gift card is from
 * @property {HTMLInputElement} toInput - Who the gift card is for
 * @property {HTMLSpanElement} fromCount - Character count for the from field
 * @property {HTMLSpanElement} toCount - Character count for the to field
 * @property {HTMLDivElement} fromError - Error container for the from field
 * @property {HTMLDivElement} toError - Error container for the to field
 * @property {HTMLInputElement} idInput - Hidden field carrying the generated line item id
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements
 */

/** Length of the generated `_id` value. */
const ID_LENGTH = 10;

/** Lowercase Crockford base32 — 32 characters, so a random byte maps onto it without bias. */
const ID_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/**
 * Generates a random id for a gift card line item.
 *
 * @returns {string} A string of {@link ID_LENGTH} characters from {@link ID_ALPHABET}.
 */
function generateId() {
  const bytes = new Uint8Array(ID_LENGTH);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, (byte) => ID_ALPHABET[byte % ID_ALPHABET.length]).join('');
}

/**
 * Collects the `_from`, `_to` and `_id` line item properties on a gift card product.
 *
 * Both visible fields are required and capped by the input's maxlength. Validation is
 * left to the browser's constraint API — ProductFormComponent only submits a valid form
 * — and this component replaces the native validation bubble with an inline message.
 *
 * `_id` is a random string generated here and refreshed after every cart update, so each
 * gift card added to the cart carries its own id even when the from / to values repeat.
 *
 * @extends {Component<GiftCardNoteRefs>}
 */
class GiftCardNote extends Component {
  requiredRefs = ['fromInput', 'toInput', 'fromCount', 'toCount', 'fromError', 'toError', 'idInput'];

  /** @type {((event: Event) => void) | null} */
  #handleInvalidBound = null;
  /** @type {(() => void) | null} */
  #clearErrorsBound = null;

  /**
   * Each field paired with the elements that report on it.
   * @returns {{input: HTMLInputElement, count: HTMLSpanElement, error: HTMLDivElement}[]}
   */
  get #fields() {
    const { fromInput, fromCount, fromError, toInput, toCount, toError } = this.refs;

    return [
      { input: fromInput, count: fromCount, error: fromError },
      { input: toInput, count: toCount, error: toError },
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    this.#handleInvalidBound = (event) => this.#handleInvalid(event);
    this.#clearErrorsBound = () => {
      this.#clearErrors();
      this.#refreshId();
    };

    this.#refreshId();

    for (const { input } of this.#fields) {
      // `invalid` doesn't bubble, so it can't go through the theme's `on:` delegation.
      input.addEventListener('invalid', this.#handleInvalidBound);
      this.#updateCharacterCount(input);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#clearErrorsBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#handleInvalidBound) {
      for (const { input } of this.#fields) {
        input.removeEventListener('invalid', this.#handleInvalidBound);
      }
      this.#handleInvalidBound = null;
    }

    if (this.#clearErrorsBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#clearErrorsBound);
      this.#clearErrorsBound = null;
    }
  }

  /**
   * Keeps the character count in step and drops an error the buyer has just resolved.
   * @param {Event} event - The input event
   */
  handleInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.#updateCharacterCount(input);

    if (input.validity.valid) this.#hideError(input);
  }

  /**
   * Replaces the browser's validation bubble with an inline message.
   * @param {Event} event - The invalid event
   */
  #handleInvalid(event) {
    event.preventDefault();

    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const requiredError = this.dataset.requiredError;
    const message = input.validity.valueMissing && requiredError ? requiredError : input.validationMessage;

    this.#showError(input, message);

    // Only the first invalid field in DOM order takes focus.
    const firstInvalid = this.#fields.find((field) => !field.input.validity.valid)?.input;
    if (firstInvalid === input) input.focus();

    if (this.refs.liveRegion) this.refs.liveRegion.textContent = message;
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {{input: HTMLInputElement, count: HTMLSpanElement, error: HTMLDivElement} | undefined}
   */
  #fieldFor(input) {
    return this.#fields.find((field) => field.input === input);
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  #showError(input, message) {
    const field = this.#fieldFor(input);
    if (!field) return;

    const text = field.error.querySelector('span');
    if (text) text.textContent = message;

    field.error.classList.remove('hidden');
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', field.error.id);
  }

  /** @param {HTMLInputElement} input */
  #hideError(input) {
    const field = this.#fieldFor(input);
    if (!field) return;

    const text = field.error.querySelector('span');
    if (text) text.textContent = '';

    field.error.classList.add('hidden');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }

  /** Hands the next gift card added to the cart its own id. */
  #refreshId() {
    this.refs.idInput.value = generateId();
  }

  #clearErrors() {
    for (const { input } of this.#fields) this.#hideError(input);

    if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
  }

  /** @param {HTMLInputElement} input */
  #updateCharacterCount(input) {
    const field = this.#fieldFor(input);
    if (!field) return;

    const template = field.count.getAttribute('data-template');
    if (!template) return;

    field.count.textContent = template
      .replace('[current]', input.value.length.toString())
      .replace('[max]', input.maxLength.toString());
  }
}

customElements.define('gift-card-note-component', GiftCardNote);
